import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

// MUI Components
import { Box, Typography, TextField } from '@mui/material'

// Components
import { BackIconButton, ContinueButton, AsynchronousContinueButton } from '../../../components/Buttons'
import StyledCard from '../../../components/StyledCard'
import Dropdown from '../../../components/Dropdown'
import AsyncAutocompleteDropDown from '../../../components/AsyncAutocompleteDropDown'

// Contexts
import { useDocumentCreation } from '../../../contexts/DocumentCreationContext'

export default function DescriptionStepComponent({
    onNextClick,
    documentName = '',
    setDocumentName,
    templateTypes = [],
    templateType,
    setTemplateType,
    loading,
    success,
    setSuccess,
    setLoading,
}) {
    const navigate = useNavigate()
    const { documentSetId } = useParams()
    const { document, updateDocument, setAllSections } = useDocumentCreation()
    
    const [name, setName] = useState(documentName || document.name)
    const [selectedTemplateValue, setSelectedTemplateValue] = useState('')

    // Transform templateTypes to dropdown options
    const rawTemplateOptions = templateTypes.map((template) => ({
        label: template.template_name,
        value: template.template_id,
        templateData: {
            template_id: template.template_id,
            template_name: template.template_name,
            source_document_name: template.source_document_name,
            url: template.url,
            detected_document_type: template.detected_document_type,
            template_file_name: template.template_file_name,
        }
    }))
    const templateOptions = rawTemplateOptions.filter(template => template.templateData.template_name !== null)

    // Find the currently selected template option
    const selectedTemplate = templateOptions.find(option => 
        templateType && option.templateData.template_id === templateType.template_id
    )

    useEffect(() => {
        if (selectedTemplate) {
            setSelectedTemplateValue(selectedTemplate.value)
        }
    }, [selectedTemplate])

    // Update parent state and context when local state changes
    useEffect(() => {
        setDocumentName(name)
        updateDocument({ name: name })
    }, [name, setDocumentName, updateDocument])

    const handleBackClick = () => {
        // Navigate back to the document set page
        navigate(`/documentSet/${documentSetId}`)
    }

    const handleNameChange = (event) => {
        setName(event.target.value)
    }

    const handleTemplateChange = (event) => {
        const selectedValue = event.target.value
        setSelectedTemplateValue(selectedValue)
        
        // Find the full template data
        const selectedOption = templateOptions.find(option => option.value === selectedValue)
        if (selectedOption) {
            const templateData = selectedOption.templateData
            setTemplateType(templateData)
            updateDocument({ templateType: templateData })
        }
    }

    const handleContinue = () => {
        if (name.trim() && selectedTemplateValue) {
            onNextClick()
        }
    }

    const isFormValid = name.trim() && selectedTemplateValue

    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            padding: '40px',
            paddingTop: '120px', // Account for fixed stepper
            '@media (max-width: 768px)': {
                paddingTop: '100px',
            },
            position: 'relative' 
        }}>
            <Box sx={{
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 999,
                width: 'clamp(600px, 60vw, 900px)',
                '@media (max-width: 768px)': {
                    width: 'calc(100vw - 32px)',
                    left: '16px',
                    transform: 'none',
                }
            }}>
                {/* Header */}
                <Box sx={{ mb: '24px' }}>
                    { /* Back Button */ }
                    <BackIconButton onClick={handleBackClick} />

                    { /* Title */ }
                    <Box>
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                fontSize: '24px',
                                fontWeight: 700,
                                color: '#374151',
                                fontStyle: 'normal',
                                lineHeight: '36px',
                            }}
                        >
                            Create a new document
                        </Typography>
                    </Box>
                </Box>

                {/* Form Content */}
                <Box sx={{ 
                    width: '100%',
                    minWidth: 'unset',
                    '@media (min-width: 769px)': {
                        minWidth: '600px',
                    }
                }}>
                    <StyledCard>
                        {/* Name Field */}
                        <Box>
                            <Typography 
                                sx={{ 
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    mb: 1
                                }}
                            >
                                Name
                            </Typography>
                            <TextField
                                fullWidth
                                // size="small"
                                value={name}
                                onChange={handleNameChange}
                                placeholder="e.g. Artos-12345 CSR"
                                // variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#FFFFFF',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        boxShadow: '0 1px 2px 0 rgb(0, 0, 0, 0.05)',
                                        '& fieldset': {
                                            borderColor: '#E5E7EB',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#D1D5DB',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#1F477D',
                                            borderWidth: '2px',
                                        },
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        padding: '12px 14px',
                                        fontSize: '14px',
                                        color: '#111827',
                                        '&::placeholder': {
                                            color: '#9CA3AF',
                                            opacity: 1,
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Template Field */}
                        <AsyncAutocompleteDropDown
                            uniqueKey="value"
                            label="Template"
                            // size="medium"
                            options={templateOptions}
                            value={selectedTemplate || null}
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    setSelectedTemplateValue(newValue.value)
                                    const templateData = newValue.templateData
                                    setTemplateType(templateData)
                                    updateDocument({ templateType: templateData })
                                    setAllSections([])
                                } else {
                                    setSelectedTemplateValue('')
                                    setTemplateType(null)
                                    updateDocument({ templateType: null })
                                    setAllSections([])
                                }
                            }}
                            loading={loading}
                            placeholder="ArtosGo CSR Template"
                        />
                    </StyledCard>
                </Box>
            </Box>
            {/* Fixed Continue Button */}
            <ContinueButton
                onClick={handleContinue}
                disabled={!isFormValid}
                text="Continue"
            />
            {/* <AsynchronousContinueButton
                onClick={handleContinue}
                disabled={!isFormValid}
                text="Continue"
                loading={loading}
                success={success}
                setSuccess={setSuccess}
                setLoading={setLoading}
            /> */}
        </Box>
    )
} 